import { createContext, useContext } from 'react';
import type { Form, AnsweredQuestion, Category, RequestOrigin } from './types';
import { v4 } from 'lib/utils/uuid';

// Actions
export const INIT = 'INIT';
export const ADD_ANSWER = 'ADD_ANSWER';
export const ADD_START_DATE = 'ADD_START_DATE';
export const SKIP_ANSWER = 'SKIP_ANSWER';
export const UPDATE_ANSWER = 'UPDATE_ANSWER';
export const GO_BACK = 'GO_BACK';
export const DISMISS = 'DISMISS';
export const RESUME_FORM = 'RESUME_FORM';
export const SHOW_SUMMARY = 'SHOW_SUMMARY';
export const REQUEST_EDIT = 'REQUEST_EDIT';
export const HIDE_SUMMARY = 'HIDE_SUMMARY';
export const FORM_COMPLETED = 'FORM_COMPLETED';
export const HIDE_RAQ_OPTIONS = 'HIDE_RAQ_OPTIONS';
export const SHOW_RAQ_OPTIONS = 'SHOW_RAQ_OPTIONS';

export type Action =
  | { type: typeof INIT; payload: State }
  | { type: typeof ADD_ANSWER; payload: AnsweredQuestion }
  | { type: typeof ADD_START_DATE; payload: AnsweredQuestion }
  | { type: typeof SKIP_ANSWER }
  | { type: typeof GO_BACK }
  | { type: typeof DISMISS }
  | {
      type: typeof RESUME_FORM;
      payload?: { origin: RequestOrigin; companyId?: number };
    }
  | {
      type: typeof SHOW_SUMMARY;
      payload: {
        origin: RequestOrigin;
        companyId?: number;
      };
    }
  | { type: typeof HIDE_SUMMARY }
  | { type: typeof REQUEST_EDIT; payload: Pick<State, 'questionIndex'> }
  | { type: typeof UPDATE_ANSWER; payload: AnsweredQuestion }
  | {
      type: typeof FORM_COMPLETED;
      showRaqOptions?: boolean;
      isInRaqEntryPointMode?: boolean;
    }
  | { type: typeof HIDE_RAQ_OPTIONS }
  | { type: typeof SHOW_RAQ_OPTIONS };

export type Dispatch = (action: Action) => void;

export type State = {
  form: Form;
  category?: Category;
  questionIndex: number;
  isFormCompleted: boolean;
  showJobRefinementModal: boolean;
  showSummary: boolean;
  answeredQuestions: Map<string, AnsweredQuestion>;
  editMode: boolean;
  searchKey?: string;
  origin?: RequestOrigin;
  companyId?: number;
  location?: string | null;
  postcode?: string | null;
  showRaqOptions?: boolean;
  serviceId?: string | null;
  serviceVersionId?: string | null;
};

export type StoredState = State & {
  answeredQuestions: [string, AnsweredQuestion][];
};

export const initialState = {
  questionIndex: 0,
  category: undefined,
  isFormCompleted: false,
  showJobRefinementModal: false,
  showSummary: false,
  searchKey: v4(),
  answeredQuestions: new Map<string, AnsweredQuestion>([]),
  editMode: false,
  showRaqOptions: false,
  origin: 'pre-select' as RequestOrigin,
  serviceId: undefined,
  serviceVersionId: undefined,
};

// Context
export const JobRefinementContext = createContext<{
  state: State | undefined;
  dispatch: Dispatch | undefined;
} | null>(null);

// Hooks
export const useJobRefinementState = () => {
  const context = useContext(JobRefinementContext);

  if (!context?.state || !context?.dispatch) {
    throw new Error(
      'Cannot use `useJobRefinementState` outside of a StoreProvider',
    );
  }

  const { state, dispatch } = context;
  return { state, dispatch };
};

// Reducer
export const jobRefinementReducer = (state: State, action: Action) => {
  switch (action.type) {
    case INIT: {
      return {
        ...state,
        ...action.payload,
      };
    }

    case ADD_ANSWER: {
      const answers = new Map(state.answeredQuestions);
      answers.set(action.payload.questionId, action.payload);
      return {
        ...state,
        answeredQuestions: answers,
        questionIndex: Math.min(
          state.questionIndex + 1,
          (state.form.questions?.length || 0) - 1,
        ),
      };
    }

    case ADD_START_DATE: {
      const answers = new Map(state.answeredQuestions);
      answers.set(action.payload.questionId, action.payload);
      return {
        ...state,
        answeredQuestions: answers,
      };
    }

    case SKIP_ANSWER: {
      return {
        ...state,
        questionIndex: Math.min(
          state.questionIndex + 1,
          (state.form.questions?.length || 0) - 1,
        ),
      };
    }

    case GO_BACK: {
      return {
        ...state,
        questionIndex: Math.max(state.questionIndex - 1, 0),
        showRaqOptions: false,
      };
    }

    case RESUME_FORM: {
      return {
        ...state,
        ...(action.payload ? action.payload : {}),
        showJobRefinementModal: true,
        showSummary: false,
      };
    }

    case DISMISS: {
      return {
        ...state,
        showJobRefinementModal: false,
        editMode: false,
        showSummary: false,
        showRaqOptions: false,
      };
    }

    case FORM_COMPLETED: {
      return {
        ...state,
        isFormCompleted: true,
        showJobRefinementModal: false,
        showSummary: true,
        editMode: false,
        ...(action.showRaqOptions
          ? {
              showJobRefinementModal: true,
              showRaqOptions: true,
              showSummary: false,
            }
          : {}),
        ...(action.isInRaqEntryPointMode
          ? {
              showSummary: false,
            }
          : {}),
      };
    }

    case REQUEST_EDIT: {
      return {
        ...state,
        showJobRefinementModal: true,
        questionIndex: action.payload.questionIndex,
        editMode: true,
      };
    }

    case SHOW_SUMMARY: {
      return {
        ...state,
        showSummary: true,
        origin: action.payload.origin,
        companyId: action.payload.companyId,
        showRaqOptions: false,
        showJobRefinementModal: false,
      };
    }

    case HIDE_SUMMARY: {
      return {
        ...state,
        showSummary: false,
      };
    }

    case HIDE_RAQ_OPTIONS: {
      return {
        ...state,
        showRaqOptions: false,
      };
    }

    case SHOW_RAQ_OPTIONS: {
      return {
        ...state,
        showRaqOptions: true,
      };
    }

    default:
      return state;
  }
};
